import React, { useCallback, useEffect, useRef } from "react"
import { Box, Flex, Input, Text } from "@chakra-ui/react"
import Icon from "@app/components/Icon"
import { useMedia } from "@app/hooks/useMedia"
import config from "../../../../config"
import { useAppContext } from "@app/providers/app"
import { useConfigContext } from "@app/providers/config"
import { navigate } from "gatsby"

type Props = {
  id?: string
  query: string
  inputPlaceholder?: string | null | undefined
  handleChange?: (query: string) => void
  handleSearch?: (query?: string) => void
  handleClear?: (query?: string) => void
  handleOpen?: (query?: boolean) => void
  isOpen?: boolean
  isHeaderSearch?: boolean
}

const SearchResultsForm: React.FC<Props> = ({
  id,
  query,
  inputPlaceholder,
  handleChange,
  handleSearch,
  handleClear,
  handleOpen,
  isOpen,
  isHeaderSearch,
}) => {
  const { isMedium } = useMedia()
  const { state, dispatch, history } = useAppContext()
  const inputRef = useRef<HTMLInputElement>(null)
  const {
    store: { subfolder },
  } = useConfigContext()

  const handleBackNavigation = () => {
    handleChange?.("")
    if (isOpen) {
      handleOpen?.(false)
    } else if (window.location.pathname.includes(config.settings.routes.SEARCH)) {
      // find last non-search page
      const prevValid = Array.isArray(history)
        ? history.reverse().find((path: string) => !path.includes(config.settings.routes.SEARCH))
        : null

      if (prevValid) {
        let targetPath = prevValid

        // remove subfolder prefix if exists
        if (subfolder) {
          const normalizedPrev = prevValid.replace(/^\/+|\/+$/g, "")
          const normalizedSub = subfolder.replace(/^\/+|\/+$/g, "")

          if (normalizedPrev.startsWith(`${normalizedSub}`)) {
            targetPath = normalizedPrev.slice(normalizedSub.length)
          }
        }

        navigate(targetPath)
      } else {
        navigate("/")
      }
    } else if (!isHeaderSearch) {
      dispatch({ type: "search", payload: !state.activeSearch })
    }
  }

  useEffect(() => {
    !isHeaderSearch && inputRef.current?.focus()
  }, [isHeaderSearch])

  const onClear = useCallback(() => {
    handleClear?.()
  }, [handleClear])

  const onSubmit = useCallback(
    (e: any) => {
      e.preventDefault()
      handleSearch?.()
      handleOpen?.(false)
    },
    [handleOpen, handleSearch]
  )

  const onChange = useCallback(
    (e: any) => {
      handleChange?.(e.target.value)
    },
    [handleChange]
  )

  return (
    <Box
      as="form"
      onSubmit={onSubmit}
      height="100%"
      width="100%"
      position={{ base: "sticky", md: "relative" }}
      top={0}
      background="primary.white"
      zIndex={1}
    >
      <Input
        {...(id ? { id } : {})}
        ref={inputRef}
        type="text"
        variant={isHeaderSearch ? "primary" : "search"}
        textAlign={{ base: "left", md: "center" }}
        placeholder={inputPlaceholder || ""}
        value={query}
        onChange={onChange}
        onClick={() => handleOpen?.(true)}
        px={isHeaderSearch ? "48px !important" : 13}
        fontSize={{ base: "1rem", sm: "1.5rem", md: 32 }}
        maxHeight={{ base: "48px", md: "52px" }}
        // #4366 pinned `primary` to a fixed 48px box; this field's oversized responsive type needs the old auto height
        {...(isHeaderSearch ? { height: "auto", py: "16px", lineHeight: "24px" } : {})}
      />

      {(query?.length > 0 || !isMedium) && (
        <Icon
          name="search"
          width={{ base: "20px", sm: "26px" }}
          height={{ base: "20px", sm: "26px" }}
          color="secondary.grey"
          ml={4.5}
          pos="absolute"
          top="50%"
          transform="translateY(-50%)"
          zIndex={1}
          cursor="pointer"
          onClick={onSubmit}
        />
      )}
      {(!isHeaderSearch || isOpen) && (
        <Box pos="absolute" top="50%" transform="translateY(-50%)" right={0} zIndex={1}>
          <Flex alignItems="center" gap={3}>
            {query?.length > 0 && isMedium && (
              <Text onClick={onClear} cursor="pointer" color="secondary.grey" size="xl" fontSize="1.5rem">
                Clear
              </Text>
            )}
            <Icon
              name="closeAlt"
              width={{ base: 10, sm: 15 }}
              height={{ base: 10, sm: 15 }}
              color="secondary.grey"
              mr={{ base: 1, md: 6 }}
              cursor="pointer"
              onClick={handleBackNavigation}
            />
          </Flex>
        </Box>
      )}
    </Box>
  )
}

export default React.memo(SearchResultsForm)
